import React, { useEffect, useRef } from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import { Form, PrimaryButton, FieldTextInput, FieldSelect } from '../../components';

import css from './ContactForm.module.css';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

/**
 * Cloudflare Turnstile widget — env-gated. Renders nothing unless
 * REACT_APP_TURNSTILE_SITE_KEY is set. On success it writes the token into the
 * final-form field "turnstileToken" via the provided change handler.
 */
const TurnstileWidget = props => {
  const { onToken } = props;
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || typeof window === 'undefined') {
      return undefined;
    }

    let cancelled = false;

    const renderWidget = () => {
      if (cancelled || !window.turnstile || !containerRef.current) {
        return;
      }
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: token => onToken(token),
        'expired-callback': () => onToken(undefined),
        'error-callback': () => onToken(undefined),
      });
    };

    if (window.turnstile) {
      renderWidget();
    } else if (!document.querySelector(`script[src="${TURNSTILE_SRC}"]`)) {
      const script = document.createElement('script');
      script.src = TURNSTILE_SRC;
      script.async = true;
      script.defer = true;
      script.onload = renderWidget;
      document.head.appendChild(script);
    } else {
      // Script tag exists but API not ready yet — poll briefly.
      const t = window.setInterval(() => {
        if (window.turnstile) {
          window.clearInterval(t);
          renderWidget();
        }
      }, 200);
      return () => {
        cancelled = true;
        window.clearInterval(t);
      };
    }

    return () => {
      cancelled = true;
    };
  }, [onToken]);

  if (!TURNSTILE_SITE_KEY) {
    return null;
  }
  return <div className={css.turnstile} ref={containerRef} />;
};

const ContactFormComponent = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        intl,
        inProgress,
        invalid,
        sendError,
        showListingPicker,
        listingOptions,
        form,
      } = fieldRenderProps;

      // Name
      const nameLabel = intl.formatMessage({ id: 'ContactPage.nameLabel' });
      const namePlaceholder = intl.formatMessage({ id: 'ContactPage.namePlaceholder' });
      const nameRequired = validators.required(
        intl.formatMessage({ id: 'ContactPage.nameRequired' })
      );

      // Email
      const emailLabel = intl.formatMessage({ id: 'ContactPage.emailLabel' });
      const emailPlaceholder = intl.formatMessage({ id: 'ContactPage.emailPlaceholder' });
      const emailRequired = validators.required(
        intl.formatMessage({ id: 'ContactPage.emailRequired' })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({ id: 'ContactPage.emailInvalid' })
      );

      // Message
      const messageLabel = intl.formatMessage({ id: 'ContactPage.messageLabel' });
      const messagePlaceholder = intl.formatMessage({ id: 'ContactPage.messagePlaceholder' });
      const messageRequired = validators.required(
        intl.formatMessage({ id: 'ContactPage.messageRequired' })
      );

      // Listing picker
      const pickerLabel = intl.formatMessage({ id: 'ContactPage.pickerLabel' });
      const pickerOptional = intl.formatMessage({ id: 'ContactPage.pickerOptional' });
      const pickerPlaceholder = intl.formatMessage({ id: 'ContactPage.pickerPlaceholder' });
      const pickerNote = intl.formatMessage({ id: 'ContactPage.pickerNote' });

      const classes = classNames(rootClassName || css.root, className);
      const submitDisabled = invalid || inProgress;

      const setTurnstileToken = token => form.change('turnstileToken', token);

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <p className={css.lead}>
            <FormattedMessage id="ContactPage.lead" />
          </p>

          {/* Honeypot — visually hidden, off-screen, not tab-reachable. Bots that
              fill it are dropped server-side (Phase 2). Humans never see it. */}
          <div className={css.honeypot} aria-hidden="true">
            <label htmlFor={formId ? `${formId}.company` : 'company'}>
              Company (leave this empty)
            </label>
            <input
              type="text"
              id={formId ? `${formId}.company` : 'company'}
              name="company"
              tabIndex={-1}
              autoComplete="off"
              onChange={e => form.change('company', e.target.value)}
            />
          </div>

          <FieldTextInput
            type="text"
            id={formId ? `${formId}.name` : 'name'}
            name="name"
            autoComplete="name"
            label={nameLabel}
            placeholder={namePlaceholder}
            validate={nameRequired}
          />

          <FieldTextInput
            className={css.field}
            type="email"
            id={formId ? `${formId}.email` : 'email'}
            name="email"
            autoComplete="email"
            label={emailLabel}
            placeholder={emailPlaceholder}
            validate={validators.composeValidators(emailRequired, emailValid)}
          />

          {showListingPicker ? (
            <div className={css.picker}>
              <FieldSelect
                className={css.field}
                id={formId ? `${formId}.listingId` : 'listingId'}
                name="listingId"
                label={
                  <>
                    {pickerLabel} <span className={css.optional}>{pickerOptional}</span>
                  </>
                }
              >
                <option value="">{pickerPlaceholder}</option>
                {listingOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </FieldSelect>
              <div className={css.pickerNote}>
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#b07a2a"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
                </svg>
                {pickerNote}
              </div>
            </div>
          ) : null}

          <FieldTextInput
            className={css.field}
            type="textarea"
            id={formId ? `${formId}.message` : 'message'}
            name="message"
            label={messageLabel}
            placeholder={messagePlaceholder}
            validate={messageRequired}
          />

          <TurnstileWidget onToken={setTurnstileToken} />

          <PrimaryButton
            className={css.submitButton}
            type="submit"
            inProgress={inProgress}
            disabled={submitDisabled}
          >
            <FormattedMessage id="ContactPage.submit" />
          </PrimaryButton>

          <p className={css.fine}>
            <FormattedMessage
              id="ContactPage.fine"
              values={{ email: <b>help@premierhorse.com</b> }}
            />
          </p>

          {sendError ? (
            <div className={css.sendError} role="alert">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9c2a22"
                strokeWidth="2.2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="9" />
                <line x1="12" y1="8" x2="12" y2="13" />
                <circle cx="12" cy="16.5" r=".6" fill="#9c2a22" />
              </svg>
              <span>
                <FormattedMessage id="ContactPage.sendError" />
              </span>
            </div>
          ) : null}
        </Form>
      );
    }}
  />
);

/**
 * Contact form — name, email (prefilled when signed in), optional listing
 * picker (signed-in sellers only), message, hidden honeypot and env-gated
 * Turnstile. Mounted inside CrownAuthShell's cream body slot, so the shell's
 * field auto-styling applies.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.inProgress - Submit in progress
 * @param {boolean} props.sendError - Whether the last send failed
 * @param {boolean} props.showListingPicker - Render the listing picker
 * @param {Array<{value:string,label:string}>} props.listingOptions - Picker options, newest first
 * @returns {JSX.Element}
 */
const ContactForm = props => {
  const intl = useIntl();
  return <ContactFormComponent {...props} intl={intl} />;
};

export default ContactForm;
